import React, { useMemo, useState } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ErrorBar,
  ResponsiveContainer
} from 'recharts';
import { getAnimeAverageRating, getWatchedEpisodeCount } from '../../utils/animeUtils';

const RatingTrendChart = ({ animes }) => {
  const { t, language } = useLanguage();
  const [useCompactScale, setUseCompactScale] = useState(true);
  const chartData = [];
  // Season order mapping for sorting
  const getSeasonOrder = (season) => {
    const seasonOrderMap = {
      '冬': 0, 'Winter': 0,
      '春': 1, 'Spring': 1,
      '夏': 2, 'Summer': 2,
      '秋': 3, 'Fall': 3
    };
    return seasonOrderMap[season] || 0;
  };

  // Convert season based on current language
  const getLocalizedSeason = (season) => {
    if (language === 'en') {
      const seasonMap = {
        '冬': 'Winter',
        '春': 'Spring', 
        '夏': 'Summer',
        '秋': 'Fall'
      };
      return seasonMap[season] || season;
    } else {
      // 日本語の場合はクールを付ける
      return `${season}クール`;
    }
  };

  // データを年・クールごとに集計
  const groupedData = {};
  animes.forEach(anime => {
    if (!anime.year || !anime.season) return;

    // detailed モードの評価も含めて平均を算出
    const avgRating = getAnimeAverageRating(anime);
    const ratedCount = getWatchedEpisodeCount(anime);

    if (avgRating <= 0 || ratedCount === 0) return;

    const key = `${anime.year}${anime.season}`;
    if (!groupedData[key]) {
      groupedData[key] = {
        year: anime.year,
        season: anime.season,
        ratings: []
      };
    }

    groupedData[key].ratings.push(avgRating);
  });

  // チャートデータに変換
  Object.values(groupedData)
    .sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return getSeasonOrder(a.season) - getSeasonOrder(b.season);
    })
    .forEach(({ year, season, ratings }) => {
      if (ratings.length === 0) return;

      const avg = ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
      const variance = ratings.reduce((sum, r) => sum + Math.pow(r - avg, 2), 0) / ratings.length;
      const stdDev = Math.sqrt(variance);

      const localizedSeason = getLocalizedSeason(season);
      chartData.push({
        name: language === 'en' ? `${year} ${localizedSeason}` : `${year}年 ${localizedSeason}`,
        average: parseFloat(avg.toFixed(2)),
        stdDev: parseFloat(stdDev.toFixed(2)),
      count: ratings.length
    });
  });

  const yAxisProps = useMemo(() => {
    if (!chartData.length || !useCompactScale) {
      return { domain: [0, 5], tickFormatter: (v) => v.toFixed(1) };
    }

    const averages = chartData.map(d => d.average);
    const minVal = Math.min(...averages);
    const maxVal = Math.max(...averages);
    const padding = 0.3;

    let minDomain = Math.max(0, Math.floor((minVal - padding) * 10) / 10);
    let maxDomain = Math.min(5, Math.ceil((maxVal + padding) * 10) / 10);

    if (maxDomain - minDomain < 0.6) {
      const mid = (minDomain + maxDomain) / 2;
      minDomain = Math.max(0, mid - 0.3);
      maxDomain = Math.min(5, mid + 0.3);
    }

    return {
      domain: [minDomain, maxDomain],
      tickFormatter: (v) => v.toFixed(1)
    };
  }, [chartData, useCompactScale]);

  if (chartData.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">📈 {t('ratingTrend')}</h2>
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">{t('dataInsufficient')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">📈 {t('ratingTrend')}</h2>
        <button
          onClick={() => setUseCompactScale(prev => !prev)}
          className="self-start sm:self-auto px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
        >
          {useCompactScale ? t('switchToFullScale') : t('switchToCompactScale')}
        </button>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis {...yAxisProps} />
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload[0]) {
                const data = payload[0].payload;
                return (
                  <div className="bg-white dark:bg-gray-700 p-3 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
                    <p className="font-bold dark:text-gray-100">{data.name}</p>
                    <p className="text-sm dark:text-gray-300">{t('average')}: {data.average}</p>
                    <p className="text-sm dark:text-gray-300">{t('standardDeviation')}: {data.stdDev}</p>
                    <p className="text-sm dark:text-gray-300">{t('animeCount')}: {data.count}</p>
                  </div>
                );
              }
              return null;
            }}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="average"
            stroke="#3B82F6"
            strokeWidth={2}
            name={t('averageRating')}
          >
            <ErrorBar dataKey="stdDev" width={4} stroke="#9CA3AF" />
          </Line>
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RatingTrendChart;
